import React, { useState, useContext, useEffect } from "react";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { useLocation } from "react-router-dom";
import "./ApplyService.css";

const API =
  process.env.REACT_APP_API_URL || "https://backend-9i6n.onrender.com";

// MAX FILE SIZE = 500 KB
const MAX_FILE_SIZE = 500 * 1024;

/* ---------- required documents per service ----------
   The `key`s below are the SINGLE SOURCE for required uploads; the old
   getRequiredFiles() now derives from this, so validation is unchanged. */
const SERVICE_DOCS = {
  "जन्म प्रमाणपत्र": [
    { key: "hospitalSlip", title: "रुग्णालयाची पावती", hint: "जन्माची रुग्णालय स्लिप", path: "M12 6v6l4 2 M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z" },
    { key: "parentsAadhaar", title: "पालकांचे आधार कार्ड", hint: "आई-वडिलांचे आधार", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
    { key: "addressProof", title: "पत्ता पुरावा", hint: "रहिवासी दाखला", path: "M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0z M12 7v6" },
  ],
  "मृत्यू प्रमाणपत्र": [
    { key: "deathSlip", title: "मृत्यू प्रमाणपत्र / रुग्णालय पावती", hint: "अधिकृत पावती", path: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z M14 2v6h6" },
    { key: "deceasedAadhaar", title: "मृत व्यक्तीचे आधार कार्ड", hint: "आधार क्रमांक", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
    { key: "applicantAadhaar", title: "अर्जदाराचे आधार कार्ड", hint: "स्वतःचे आधार", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
  ],
  "उत्पन्न प्रमाणपत्र": [
    { key: "aadhaar", title: "अर्जदाराचे आधार कार्ड", hint: "स्वतःचा आधार कार्ड", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
    { key: "rationCard", title: "रेशन कार्ड", hint: "कुटुंबाचे रेशन कार्ड", path: "M4 4h16v16H4z M8 8h8 M8 12h8 M8 16h5" },
    { key: "incomeProof", title: "उत्पन्नाचा पुरावा", hint: "पगार स्लिप / शेती उत्पन्न दाखला", path: "M12 1v22 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" },
  ],
  "रहिवासी प्रमाणपत्र": [
    { key: "aadhaar", title: "अर्जदाराचे आधार कार्ड", hint: "पत्ता असलेले आधार कार्ड", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
    { key: "electricityBill", title: "वीज बिल", hint: "मागील ३ महिन्यांतील लाईट बिल", path: "M13 2 3 14h7v8l10-12h-7z" },
    { key: "rationCard", title: "रेशन कार्ड", hint: "सध्याच्या पत्त्याचे रेशन कार्ड", path: "M4 4h16v16H4z M8 8h8 M8 12h8 M8 16h5" },
  ],
  "विवाह प्रमाणपत्र": [
    { key: "brideGroomAadhaar", title: "वराचे आधार कार्ड", hint: "नवऱ्याचे आधार कार्ड", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
    { key: "brideAadhaar", title: "वधूचे आधार कार्ड", hint: "नवरीचे आधार कार्ड", path: "M3 5h18v14H3z M7 9h4 M7 13h10" },
    { key: "ageProof", title: "वयाचा पुरावा", hint: "जन्म प्रमाणपत्र / शाळेचा दाखला", path: "M8 2v4M16 2v4M3 10h18 M3 6h18v14H3z" },
    { key: "marriagePhoto", title: "विवाह समारंभाचे छायाचित्र", hint: "लग्नाच्या वेळचा फोटो", path: "M3 5h18v14H3z M8 11a2 2 0 1 0 0-4 2 2 0 0 0 0 4z M21 17l-6-6-9 9" },
    { key: "witnessAadhaar", title: "साक्षीदारांचे आधार कार्ड", hint: "दोन साक्षीदारांचे आधार", path: "M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M22 21v-2a4 4 0 0 0-3-3.9" },
  ],
};

const SvgIcon = ({ d, stroke = "#0F5C2E", size = 20, sw = 1.9 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const ApplyService = () => {
  const { token } = useContext(AuthContext);
  const location = useLocation();

  const [formData, setFormData] = useState({
    serviceType: "",
    fullName: "",
    address: "",
    mobile: "",
    deceasedName: "",
    dateOfDeath: "",
  });

  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [fieldErrors, setFieldErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [loading, setLoading] = useState(false);

  /* CENTER POPUP (replaces native alert) */
  const [popup, setPopup] = useState({ show: false, type: "success", icon: "", message: "" });

  const showPopup = (type, icon, message, autoHideMs = 0) => {
    setPopup({ show: true, type, icon, message });
    if (autoHideMs) {
      setTimeout(() => setPopup((p) => ({ ...p, show: false })), autoHideMs);
    }
  };

  const closePopup = () => setPopup((p) => ({ ...p, show: false }));

  /* PREFILL SERVICE */
  useEffect(() => {
    window.scrollTo(0, 0);
    const params = new URLSearchParams(location.search);
    const serviceFromUrl = params.get("service");
    if (serviceFromUrl) {
      setFormData((p) => ({ ...p, serviceType: serviceFromUrl }));
    }
  }, [location.search]);

  /* VALIDATION RULES */
  const validateField = (name, value) => {
    let error = "";

    switch (name) {
      case "serviceType":
        if (!value) error = "कृपया सेवा निवडा";
        break;

      case "fullName":
        if (!value.trim()) {
          error = "पूर्ण नाव आवश्यक आहे";
        } else if (value.trim().length < 3) {
          error = "नाव किमान 3 अक्षरांचे असावे";
        } else if (!/^[a-zA-Zà-ÿĀ-ſА-яа-яऀ-ॿ\s]+$/.test(value)) {
          error = "नावात फक्त अक्षरे असावीत";
        }
        break;

      case "address":
        if (!value.trim()) {
          error = "पत्ता आवश्यक आहे";
        } else if (value.trim().length < 10) {
          error = "पत्ता किमान 10 अक्षरांचा असावा";
        }
        break;

      case "mobile":
        if (!value) {
          error = "मोबाईल क्रमांक आवश्यक आहे";
        } else if (!/^\d{10}$/.test(value)) {
          error = "कृपया 10 अंकी मोबाईल क्रमांक टाका";
        } else if (!/^[5-9]\d{9}$/.test(value)) {
          error = "भारतीय मोबाईल क्रमांक 7,8,9 ने सुरू होतो";
        }
        break;

      case "deceasedName":
        if (formData.serviceType === "मृत्यू प्रमाणपत्र") {
          if (!value.trim()) {
            error = "मृत व्यक्तीचे नाव आवश्यक आहे";
          } else if (value.trim().length < 3) {
            error = "नाव किमान 3 अक्षरांचे असावे";
          }
        }
        break;

      case "dateOfDeath":
        if (formData.serviceType === "मृत्यू प्रमाणपत्र") {
          if (!value) {
            error = "मृत्यूची तारीख आवश्यक आहे";
          } else {
            const selectedDate = new Date(value);
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            if (selectedDate > today) {
              error = "भविष्यातील तारीख निवडू शकत नाही";
            } else {
              const hundredYearsAgo = new Date();
              hundredYearsAgo.setFullYear(today.getFullYear() - 100);
              if (selectedDate < hundredYearsAgo) {
                error = "तारीख 100 वर्षांपेक्षा जुनी असू शकत नाही";
              }
            }
          }
        }
        break;

      default:
        break;
    }

    return error;
  };

  /* INPUT CHANGE WITH VALIDATION */
  const handleChange = (e) => {
    const { name, value } = e.target;
    const nextValue = name === "mobile" ? value.replace(/\D/g, "").slice(0, 10) : value;
    setFormData({ ...formData, [name]: nextValue });

    // Validate if field was touched
    if (touched[name]) {
      const error = validateField(name, nextValue);
      setFieldErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  /* HANDLE BLUR - Mark field as touched */
  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setFieldErrors((prev) => ({ ...prev, [name]: error }));
  };

  /* CHANGE SERVICE (dropdown) — preselected from the Services page,
     changeable here in the rare case the user wants a different one */
  const handleServiceChange = (e) => {
    const value = e.target.value;
    setFormData((p) => ({
      ...p,
      serviceType: value,
      deceasedName: "",
      dateOfDeath: "",
    }));
    setFiles({});
    setFileErrors({});
    setTouched((t) => ({ ...t, serviceType: true }));
    setFieldErrors((prev) => ({ ...prev, serviceType: value ? "" : "कृपया सेवा निवडा" }));
  };

  /* FILE VALIDATION */
  const handleFileChange = (e) => {
    const { name, files: selectedFiles } = e.target;
    const file = selectedFiles[0];

    if (!file) {
      setFileErrors((p) => ({ ...p, [name]: "" }));
      setFiles((p) => {
        const newFiles = { ...p };
        delete newFiles[name];
        return newFiles;
      });
      return;
    }

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];

    if (!allowedTypes.includes(file.type)) {
      setFileErrors((p) => ({
        ...p,
        [name]: "फक्त JPG, JPEG, PNG किंवा PDF फाईल अपलोड करा",
      }));
      e.target.value = "";
      setFiles((p) => {
        const newFiles = { ...p };
        delete newFiles[name];
        return newFiles;
      });
      return;
    }

    if (file.size > MAX_FILE_SIZE) {
      setFileErrors((p) => ({
        ...p,
        [name]: "फाईलचा आकार 500 KB पेक्षा जास्त नसावा",
      }));
      e.target.value = "";
      setFiles((p) => {
        const newFiles = { ...p };
        delete newFiles[name];
        return newFiles;
      });
      return;
    }

    setFileErrors((p) => ({ ...p, [name]: "" }));
    setFiles((p) => ({ ...p, [name]: file }));
  };

  /* GET REQUIRED FILES BASED ON SERVICE TYPE (derived from SERVICE_DOCS) */
  const getRequiredFiles = () =>
    (SERVICE_DOCS[formData.serviceType] || []).map((d) => d.key);

  /* VALIDATE ALL FIELDS BEFORE SUBMIT */
  const validateAllFields = () => {
    const errors = {};

    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) errors[key] = error;
    });

    const requiredFiles = getRequiredFiles();
    requiredFiles.forEach((fileName) => {
      if (!files[fileName]) {
        errors[fileName] = "कृपया आवश्यक फाईल अपलोड करा";
      }
    });

    return errors;
  };

  /* SUBMIT */
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Mark all fields as touched
    const allFields = [...Object.keys(formData), ...getRequiredFiles()];
    const touchedState = {};
    allFields.forEach((field) => {
      touchedState[field] = true;
    });
    setTouched(touchedState);

    // Validate all fields
    const errors = validateAllFields();

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setFileErrors(errors);
      showPopup("error", "⚠️", "कृपया सर्व आवश्यक माहिती भरा आणि चुका दुरुस्त करा");
      return;
    }

    setLoading(true);

    const data = new FormData();
    Object.entries(formData).forEach(([k, v]) => data.append(k, v));
    Object.entries(files).forEach(([k, f]) => data.append(k, f));

    try {
      await axios.post(`${API}/api/services/apply`, data, {
        headers: { Authorization: `Bearer ${token}` },
      });

      showPopup("success", "✅", "अर्ज यशस्वीरीत्या सादर झाला!", 3000);

      // Reset form
      setFormData({
        serviceType: "",
        fullName: "",
        address: "",
        mobile: "",
        deceasedName: "",
        dateOfDeath: "",
      });
      setFiles({});
      setFileErrors({});
      setFieldErrors({});
      setTouched({});

      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => {
        input.value = "";
      });
    } catch (error) {
      showPopup("error", "❌", "अर्ज सादर होऊ शकला नाही. कृपया पुन्हा प्रयत्न करा");
    } finally {
      setLoading(false);
    }
  };

  /* ---------- derived view state (summary sidebar) ---------- */
  const service = formData.serviceType;
  const isDeath = service === "मृत्यू प्रमाणपत्र";
  const docList = SERVICE_DOCS[service] || [];

  const applicantOk =
    !!formData.fullName.trim() &&
    /^[5-9]\d{9}$/.test(formData.mobile) &&
    formData.address.trim().length >= 10 &&
    (!isDeath || (!!formData.deceasedName.trim() && !!formData.dateOfDeath));

  const docsDone = docList.filter((d) => files[d.key]).length;
  const docsOk = docList.length > 0 && docsDone === docList.length;

  const steps = [
    { label: "सेवा निवडली", ok: !!service },
    { label: "अर्जदार माहिती", ok: applicantOk },
    { label: docList.length ? `कागदपत्रे (${docsDone}/${docList.length})` : "कागदपत्रे", ok: docsOk },
  ];
  const progressPct = Math.round((steps.filter((s) => s.ok).length / 3) * 100);

  return (
    <div className="apply-service-container">
      {/* ===== BANNER ===== */}
      <section className="as-banner">
        <div className="as-banner-inner">
          <div className="as-crumb">
            <span>मुख्यपृष्ठ</span><span className="sep">/</span>
            <span>सेवा</span><span className="sep">/</span>
            <span className="cur">अर्ज</span>
          </div>
          <h1>सेवेसाठी अर्ज करा</h1>
          <p>प्रमाणपत्रासाठी खालील माहिती भरा व आवश्यक कागदपत्रे अपलोड करा. सर्व फाईल्स JPG, PNG किंवा PDF (कमाल ५०० KB) असाव्यात.</p>
        </div>
      </section>

      {/* ===== GRID (form + sidebar wrapped in one form) ===== */}
      <form className="as-grid" onSubmit={handleSubmit} encType="multipart/form-data" noValidate>
        {/* LEFT: STEPS */}
        <div className="as-main">
          {/* STEP 1 — APPLICANT (service preselected via dropdown) */}
          <div className="as-card">
            <div className="as-step-head">
              <span className="as-step-num">१</span>
              <div>
                <h2>अर्ज तपशील</h2>
                <p>सेवा निवडलेली आहे — आवश्यक असल्यास बदला व आपला तपशील भरा</p>
              </div>
            </div>

            <div className="as-field-grid">
              <div className="as-field as-field-full">
                <label>सेवा <span className="req">*</span></label>
                <select
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleServiceChange}
                  onBlur={handleBlur}
                  className={`as-select ${fieldErrors.serviceType ? "error" : ""}`}
                >
                  <option value="">-- सेवा निवडा --</option>
                  {Object.keys(SERVICE_DOCS).map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {fieldErrors.serviceType && <p className="as-error">{fieldErrors.serviceType}</p>}
              </div>

              <div className="as-field">
                <label>पूर्ण नाव <span className="req">*</span></label>
                <input
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="उदा. राजेश कुमार शर्मा"
                  className={fieldErrors.fullName ? "error" : ""}
                />
                {fieldErrors.fullName && <p className="as-error">{fieldErrors.fullName}</p>}
              </div>

              <div className="as-field">
                <label>मोबाईल क्रमांक <span className="req">*</span></label>
                <input
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  maxLength={10}
                  inputMode="numeric"
                  placeholder="उदा. 9876543210"
                  className={fieldErrors.mobile ? "error" : ""}
                />
                {fieldErrors.mobile && <p className="as-error">{fieldErrors.mobile}</p>}
              </div>

              <div className="as-field as-field-full">
                <label>पत्ता <span className="req">*</span></label>
                <input
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  placeholder="घर क्रमांक, गाव/शहर, तालुका, जिल्हा"
                  className={fieldErrors.address ? "error" : ""}
                />
                {fieldErrors.address && <p className="as-error">{fieldErrors.address}</p>}
              </div>

              {isDeath && (
                <>
                  <div className="as-field">
                    <label>मृत व्यक्तीचे नाव <span className="req">*</span></label>
                    <input
                      name="deceasedName"
                      value={formData.deceasedName}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      placeholder="उदा. सुरेश पाटील"
                      className={fieldErrors.deceasedName ? "error" : ""}
                    />
                    {fieldErrors.deceasedName && <p className="as-error">{fieldErrors.deceasedName}</p>}
                  </div>

                  <div className="as-field">
                    <label>मृत्यूची तारीख <span className="req">*</span></label>
                    <input
                      type="date"
                      name="dateOfDeath"
                      value={formData.dateOfDeath}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      className={fieldErrors.dateOfDeath ? "error" : ""}
                    />
                    {fieldErrors.dateOfDeath && <p className="as-error">{fieldErrors.dateOfDeath}</p>}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* STEP 2 — DOCUMENTS */}
          <div className="as-card">
            <div className="as-step-head">
              <span className="as-step-num">२</span>
              <div>
                <h2>आवश्यक कागदपत्रे</h2>
                <p>प्रत्येक फाईल कमाल ५०० KB · JPG/PNG/PDF</p>
              </div>
            </div>

            {!service ? (
              <div className="as-doc-empty">
                प्रथम वरून सेवा निवडा — त्यानुसार आवश्यक कागदपत्रांची यादी येथे दिसेल.
              </div>
            ) : (
              <div className="as-doc-list">
                {docList.map((d) => {
                  const done = !!files[d.key];
                  const err = fileErrors[d.key];
                  return (
                    <div key={d.key}>
                      <label className={`as-doc-row ${done ? "done" : ""} ${err ? "err" : ""}`}>
                        <span className="as-doc-icon">
                          {done ? (
                            <SvgIcon d="M20 6 9 17l-5-5" stroke="#0F5C2E" size={20} sw={2.4} />
                          ) : (
                            <SvgIcon d={d.path} stroke="#0F5C2E" size={20} />
                          )}
                        </span>
                        <span className="as-doc-text">
                          <span className="as-doc-title">{d.title}</span>
                          <span className="as-doc-hint">
                            {done ? files[d.key].name : d.hint}
                          </span>
                        </span>
                        <span className={`as-doc-btn ${done ? "done" : ""}`}>
                          {done ? "बदला" : "अपलोड"}
                        </span>
                        <input
                          type="file"
                          name={d.key}
                          onChange={handleFileChange}
                          accept="image/png,image/jpeg,application/pdf"
                          hidden
                        />
                      </label>
                      {err && <p className="as-error as-error-doc">{err}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: SUMMARY */}
        <aside className="as-side">
          <div className="as-summary">
            <div className="as-summary-head">
              <div className="as-summary-eyebrow">निवडलेली सेवा</div>
              <div className="as-summary-service">{service || "निवडलेली नाही"}</div>
            </div>
            <div className="as-summary-body">
              <div>
                <div className="as-progress-row">
                  <span>पूर्णता</span>
                  <span className="pct">{progressPct}%</span>
                </div>
                <div className="as-progress-track">
                  <div className="as-progress-fill" style={{ width: `${progressPct}%` }} />
                </div>
              </div>

              <div className="as-checklist">
                {steps.map((s, i) => (
                  <div className="as-check-item" key={i}>
                    <span className={`as-check-dot ${s.ok ? "ok" : ""}`}>
                      {s.ok ? <SvgIcon d="M20 6 9 17l-5-5" stroke="#fff" size={11} sw={3} /> : null}
                    </span>
                    <span className={`as-check-label ${s.ok ? "ok" : ""}`}>{s.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <button type="submit" className="as-submit" disabled={loading}>
            {loading ? (
              <>
                <span className="as-spinner" /> सादर होत आहे...
              </>
            ) : (
              <>
                अर्ज सादर करा
                <SvgIcon d="M5 12h14M13 6l6 6-6 6" stroke="currentColor" size={17} sw={2.2} />
              </>
            )}
          </button>

          <div className="as-note">
            <SvgIcon d="M12 16v-4M12 8h.01" stroke="#1466A8" size={16} sw={2} />
            <span>अर्ज सादर केल्यानंतर आपल्या अर्जाची स्थिती "माझे खाते" मध्ये पाहता येईल.</span>
          </div>
        </aside>
      </form>

      {/* ===== CENTER POPUP — success / error in middle of screen ===== */}
      {popup.show && (
        <div className="toast-overlay show" onClick={closePopup}>
          <div
            className={`toast ${popup.type}`}
            role="alertdialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="toast-icon">{popup.icon}</div>
            <div className="toast-message">{popup.message}</div>
            <button type="button" className="toast-btn" onClick={closePopup}>
              ठीक आहे
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplyService;
